// src/ui/Configurator.tsx
import React, { useState, useEffect } from 'react';

interface ConfiguratorProps {
  largeur: number;
  nbCam: number;
  isMobile: boolean;
}

type OfferType = 'essentielle' | 'performance' | 'industriel';

export const Configurator: React.FC<ConfiguratorProps> = ({ largeur, nbCam, isMobile }) => {
  const [offer, setOffer] = useState<OfferType>('performance');
  const [showForm, setShowForm] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tel: '',
    company: '',
    role: '',
    captchaInput: '',
    honeypot: '' // champ invisible anti-spam
  });
  const [captchaCode, setCaptchaCode] = useState('');

  const emeraldGreen = '#58a08c';
  const darkNavy = '#2c3e50';

  const offersData: Record<OfferType, any> = {
    essentielle: { title: 'Essentielle', icon: '⭐', price: '180', year: '1 950 TND/an', features: ['Détection standards', 'Interface opérateur', 'Historique local', 'IA supervisée', 'Rapports rouleau', 'Support'] },
    performance: { title: 'Performance', icon: '⭐⭐', price: '280', year: '3 050 TND/an', plus: 'Inclut Essentielle +', features: ['IA non supervisée', 'Reporting avancé', 'Stats filtrables', 'Accès distant', 'Stockage structuré'] },
    industriel: { title: 'Privilège', icon: '⭐⭐⭐', price: '480', year: '4 100 TND/an', plus: 'Inclut Performance +', features: ['Serveur dédié', 'Accès illimité', 'IA Recommandation', 'App Mobile', 'Support prioritaire & évolutions continues'] }
  };

  // --- CAPTCHA ---
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 5; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    setCaptchaCode(result);
  };

  useEffect(() => {
    if (showForm) generateCaptcha();
  }, [showForm]);

  // --- SUBMIT FORM ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.honeypot) return;
    if (formData.captchaInput.toUpperCase() !== captchaCode) {
      alert("Captcha incorrect");
      generateCaptcha();
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('https://vizmerald.com/api/contact-config.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, config: { nbCam, largeur, offer } }),
      });
      if (res.ok) setSent(true);
      else alert("Erreur serveur.");
    } catch {
      alert("Erreur serveur.");
    } finally { setLoading(false); }
  };

  const containerStyle: React.CSSProperties = {
    position: isMobile ? 'relative' : 'absolute',
    right: isMobile ? 'auto' : '2%',
    top: isMobile ? 'auto' : '50%',
    transform: isMobile ? 'translateX(-5%) translateY(15%)' : 'translateX(-20%) translateY(-10%)',
    width: isMobile ? '100%' : '850px',
    padding: isMobile ? '12px' : '25px',
    borderRadius: '24px',
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(3px)',
    boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
    fontFamily: 'Inter, sans-serif',
    pointerEvents: 'all',
    zIndex: 100,
    border: '1px solid #fff'
  };

  if (sent) return (
    <aside style={{ ...containerStyle, width: '380px', textAlign: 'center' }}>
      <div style={{ fontSize: '3.5rem' }}>✅</div>
      <h3 style={{ color: darkNavy, marginBottom: '10px' }}>Données envoyées</h3>
      <p style={{ fontSize: '0.9rem', color: '#64748b' }}>
        Merci M./Mme {formData.name}. <br />
        <br />
        Vous seriez contacté(e) sous 48h 
        <br />
        pour recevoir votre offre personnalisée.
      </p>
      <button
        onClick={() => { setSent(false); setShowForm(false); }}
        style={{
          marginTop: '20px',
          background: darkNavy,
          color: '#fff',
          border: 'none',
          padding: '12px 25px',
          borderRadius: '10px',
          cursor: 'pointer',
          fontWeight: 700
        }}
      >
        Retour au site
      </button>
    </aside>
  );

  return (
    <aside style={containerStyle}>
      {!showForm ? (
        <>
          {/* --- TITRE CONFIGURATION --- */}
          <h3 style={{
            margin: '0 0 15px 0',
            fontSize: isMobile ? '1.1rem' : '1.4rem',
            color: 'white',
            WebkitTextStroke: '0.01px ' + darkNavy,
            fontWeight: 900,
            fontFamily: 'X360'
          }}>
            Configuration Matérielle
          </h3>

          {/* --- INFOS MATÉRIEL FIXE --- */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '12px',
            marginBottom: isMobile ? '15px' : '25px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '10px' : '15px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                <img src="./images/camera.png" alt="Camera" style={{ fontSize: '1.8rem', width: '40px', height: '40px', objectFit: 'contain' }} />
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 800 }}>{nbCam} Caméras industrielles / {largeur}cm</div>
                <div style={{ fontSize: '0.7rem', color: emeraldGreen, fontWeight: 700 }}>Investissement Matériel Fixe</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? '10px' : '15px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <img src="./images/compute.png" alt="Camera" style={{ fontSize: '1.8rem', width: '40px', height: '40px', objectFit: 'contain' }} />

              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 800 }}>Composants IA & Liaison</div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Intégration Machine Variable</div>
              </div>
            </div>
          </div>

          {/* --- PLANS SAAS --- */}
          <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: darkNavy, fontWeight: 800 }}>Plans d'Accès Logiciel (SaaS)</h4>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: isMobile ? '8px' : '15px' }}>
            {(Object.entries(offersData) as [OfferType, any][]).map(([key, data]) => {
              const active = offer === key;
              return (
                <div key={key} onClick={() => setOffer(key)} style={{
                  padding: isMobile ? '12px' : '20px',
                  borderRadius: '18px',
                  border: active ? `2px solid ${emeraldGreen}` : '1px solid #f1f5f9',
                  background: active ? 'rgba(88, 160, 140, 0.04)' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: active ? '0 10px 20px rgba(0,0,0,0.05)' : '0 4px 12px rgba(0,0,0,0.03)',
                  transform: active ? 'scale(1.02)' : 'scale(1)',
                }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: active ? emeraldGreen : darkNavy }}>
                    {data.icon} {data.title}
                  </div>
                  {data.plus && <div style={{ fontSize: '0.6rem', fontWeight: 700, color: emeraldGreen, marginBottom: '8px' }}>{data.plus}</div>}
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {data.features.map((f: string, i: number) => (
                      <li key={i} style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', gap: '5px' }}>
                        <span style={{ color: emeraldGreen }}>✔</span> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* --- BOUTON POUR OUVRIR FORMULAIRE --- */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
            <p style={{ fontSize: '0.65rem', color: '#94a3b8', maxWidth: '60%' }}>* Devis final ajusté selon l'installation et l'adaptation machine.</p>
            <button onClick={() => setShowForm(true)} style={{
              padding: '14px 28px',
              borderRadius: '12px',
              border: 'none',
              background: darkNavy,
              color: 'white',
              fontWeight: 800,
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              Recevez votre devis
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit} style={{ padding: isMobile ? '0' : '10px' }}>
          {/* --- BOUTON RETOUR --- */}
          <button
            type="button"
            onClick={() => setShowForm(false)}
            style={{
              background: 'none',
              border: 'none',
              color: emeraldGreen,
              cursor: 'pointer',
              fontSize: '0.85rem',
              marginBottom: '15px'
            }}
          >
            ← Modifier ma configuration
          </button>

          <h3 style={{ margin: '0 0 5px 0', color: darkNavy, fontSize: '1.3rem' }}>Recevoir l'offre détaillée</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>Solution configurée pour <b>{nbCam} caméras </b> en pack <b>{offer}</b>.</p>

          {/* HONEYPOT */}
          <input type="text" name="honeypot" style={{ display: 'none' }} value={formData.honeypot} onChange={e => setFormData({ ...formData, honeypot: e.target.value })} />

          {/* CHAMPS ESSENTIELS EN GRILLE 2 COLONNES */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <input required placeholder="Nom complet*" style={inputStyle} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            <input required type="email" placeholder="Email*" style={inputStyle} value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
            <input required type="tel" placeholder="Téléphone* (ex: +216 ...)" style={inputStyle} value={formData.tel} onChange={e => setFormData({ ...formData, tel: e.target.value })} />
            <input required placeholder="Entreprise*" style={inputStyle} value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} />
            <input required placeholder="Fonction / rôle*" style={inputStyle} value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} />
            <div /> {/* vide pour compléter la grille 3x2 */}
          </div>

          {/* CAPTCHA */}
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '0.75rem', fontWeight: 800, color: darkNavy }}>Validation de sécurité</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 900, letterSpacing: '4px', background: darkNavy, color: '#fff', padding: '6px 15px', borderRadius: '6px', fontStyle: 'italic' }}>{captchaCode}</span>
              <input required placeholder="Copiez le code" style={{ ...inputStyle, marginBottom: 0, flex: 1 }} value={formData.captchaInput} onChange={e => setFormData({ ...formData, captchaInput: e.target.value })} />
              <button type="button" onClick={generateCaptcha} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>🔄</button>
            </div>
          </div>

          <button disabled={loading} type="submit" style={{ width: '100%', padding: '16px', borderRadius: '12px', border: 'none', background: emeraldGreen, color: 'white', fontWeight: 800, fontSize: '1.1rem', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Enregistrement...' : 'Avoir mon offre personnalisée'}
          </button>
        </form>
      )}
    </aside>
  );
};

const inputStyle: React.CSSProperties = {
  width: '95%',
  padding: '12px',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  marginBottom: '0',
  fontSize: '0.95rem',
  transition: 'all 0.2s',
  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
  outline: 'none'
};
